/**
 * Module dependencies.
 */

import { Container } from 'src/components/core/layout/container';
import { Lead, Title } from './homepage/styles';
import { ModalPortal } from 'src/components/core/modal/portal';
import { ReactNode, useEffect, useState } from 'react';
import { RoundButton } from 'src/components/core/buttons/round-button';
import { media } from 'src/styles/utils/media';
import { prop } from 'styled-tools';
import { useRouter } from 'next/router';
import closeSvg from 'src/assets/svg/close.svg';
import styled from 'styled-components';

/**
 * `Props` type;
 */

type Props = {
  aside: ReactNode;
  children: ReactNode;
  className?: string;
  closeButtonLink: string;
  isContacts?: boolean;
  subtitle: string;
  title: string;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.section<Pick<Props, 'isContacts'>>`
  display: grid;
  grid-row-gap: 3rem;
  grid-template-areas: 'header' 'aside' 'form';
  grid-template-columns: auto;
  justify-self: center;
  padding: calc(var(--navbar-height) + 3rem) 0 2.5rem;
  width: 100%;

  ${({ isContacts }) =>
    isContacts
      ? media.min.md`
    grid-template-areas: 'header header' 'aside form';
    grid-template-columns: repeat(2, 1fr);
    padding: calc(var(--navbar-height) + 5rem) 0 8rem;
  `
      : media.min.md`
    grid-template-areas: 'header header header' 'aside . form';
    grid-template-columns: 4fr 1fr 5fr;
    padding: calc(var(--navbar-height) + 5rem) 0 8rem;
  `};
`;

/**
 * `CloseButton` styled component.
 */

const CloseButton = styled(RoundButton)`
  position: fixed;
  right: var(--navbar-gutter);
  top: 32px;
  z-index: var(--z-index-modal-underlay);

  ${media.min.sm`
    top: 44px;
  `}
`;

/**
 * `GridArea` styled component.
 */

const GridArea = styled.div<{ name: 'header' | 'aside' | 'form' }>`
  grid-area: ${prop('name')};
`;

/**
 * `StyledLead` styled component.
 */

const StyledLead = styled(Lead)`
  ${media.max.sm`
    margin-bottom: 0;
  `}
`;

/**
 * `PageModalLayout` component.
 */

export function PageModalLayout(props: Props) {
  const {
    aside,
    children,
    className,
    closeButtonLink,
    isContacts,
    subtitle,
    title
  } = props;

  const router = useRouter();
  const [isOpen, setOpen] = useState<boolean>(false);

  useEffect(() => setOpen(true), []);

  useEffect(() => {
    const handleRouteChange = () => {
      setOpen(false);
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router.events]);

  return (
    <>
      {isOpen && (
        <ModalPortal
          isOpen={isOpen}
          preventBodyScroll={false}
        >
          <CloseButton
            aria-label={'Close'}
            href={closeButtonLink}
            icon={closeSvg}
          />
        </ModalPortal>
      )}

      <Container>
        <Wrapper
          className={className}
          isContacts={isContacts}
        >
          <GridArea name={'header'}>
            <Title as={'h2'}>{subtitle}</Title>

            <StyledLead as={'h3'}>{title}</StyledLead>
          </GridArea>

          <GridArea
            as={'aside'}
            name={'aside'}
          >
            {aside}
          </GridArea>

          <GridArea name={'form'}>{children}</GridArea>
        </Wrapper>
      </Container>
    </>
  );
}
